if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();
        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const formData  = new FormData(this.form);
        const variantId = parseInt(formData.get('id'), 10);
        const addQty    = parseInt(formData.get('quantity') || '1', 10);

        /* Collect submitted line-item properties (skip private _ keys) */
        const submittedProps = {};
        for (const [key, val] of formData.entries()) {
          const m = key.match(/^properties\[(.+)\]$/);
          if (m && m[1].charAt(0) !== '_') submittedProps[m[1]] = val;
        }

        /* ── Pre-check cart: increment existing item instead of duplicating ── */
        fetch('/cart.js')
          .then((r) => r.json())
          .then((cart) => {
            const existing = this.findMatchingCartItem(cart.items, variantId, submittedProps);
            if (existing) {
              this.incrementCartItem(existing.key, existing.quantity + addQty, variantId, formData, evt);
            } else {
              this.addCartItem(formData, evt);
            }
          })
          .catch(() => {
            /* Cart fetch failed — fall through to standard add */
            this.addCartItem(formData, evt);
          });
      }

      /* ─────────────────────────────────────────────────────────────────
       * findMatchingCartItem
       *
       * Returns the first cart item that matches variantId AND has
       * compatible properties.
       *
       * "Compatible" means:
       *   a) The submitted form has NO filled-in properties (customer
       *      left the engraving blank on re-visit) → match ANY item
       *      with the same variant so we increment it rather than
       *      create a blank-properties duplicate.
       *   b) The submitted properties match the existing item's
       *      non-empty properties exactly.
       * ───────────────────────────────────────────────────────────────── */
      findMatchingCartItem(items, variantId, submittedProps) {
        const hasContent = Object.values(submittedProps).some((v) => v !== '');

        return (
          items.find((item) => {
            if (item.variant_id !== variantId) return false;

            /* Case (a): form is blank — match first occurrence of this variant */
            if (!hasContent) return true;

            /* Case (b): compare non-empty props from both sides */
            const existingNorm = Object.entries(item.properties || {})
              .filter(([k, v]) => k.charAt(0) !== '_' && v !== '')
              .sort(([a], [b]) => a.localeCompare(b));

            const submittedNorm = Object.entries(submittedProps)
              .filter(([, v]) => v !== '')
              .sort(([a], [b]) => a.localeCompare(b));

            return JSON.stringify(submittedNorm) === JSON.stringify(existingNorm);
          }) || null
        );
      }

      /* ─────────────────────────────────────────────────────────────────
       * incrementCartItem
       *
       * Uses /cart/change.js to update an existing line item's quantity.
       * Requests the same sections as a normal add so the cart
       * notification / drawer renders with fresh HTML.
       * ───────────────────────────────────────────────────────────────── */
      incrementCartItem(lineKey, newQty, variantId, formData, evt) {
        const sectionIds = this.cart
          ? this.cart.getSectionsToRender().map((s) => s.id)
          : [];

        const body = JSON.stringify({
          id: lineKey,
          quantity: newQty,
          sections: sectionIds,
          sections_url: window.location.pathname,
        });

        fetch(routes.cart_change_url, { ...fetchConfig(), ...{ body } })
          .then((r) => r.json())
          .then((response) => {
            if (response.errors) {
              this.handleErrorMessage(
                typeof response.errors === 'string' ? response.errors : response.description
              );
              return;
            }
            if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            CartPerformance.measure('add:paint-updated-sections', () => {
              this.cart.renderContents(response);
            });

            if (!this.error) {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: String(variantId),
                cartData: response,
              });
            }
          })
          .catch((e) => console.error(e))
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart?.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');
            CartPerformance.measureFromEvent('add:user-action', evt);
          });
      }

      /* ─────────────────────────────────────────────────────────────────
       * addCartItem — original add-to-cart logic, preserved exactly.
       * Only called when no matching item exists in cart.
       * ───────────────────────────────────────────────────────────────── */
      addCartItem(formData, evt) {
        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure('add:paint-updated-sections', () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure('add:paint-updated-sections', () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => console.error(e))
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart?.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');
            CartPerformance.measureFromEvent('add:user-action', evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage =
          this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);
        if (errorMessage) this.errorMessage.textContent = errorMessage;
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
